// Struktur-Edits am MusicXML (Löschen, Teilen, Zusammenführen, Ganztakt-Löschen samt
// Attribut-Kaskade) – aus `lyrics.ts` herausgelöst: dort blieb der Gesangstext, hierher kam
// alles, was die NOTEN-STRUKTUR eines Takts verändert. Die übergreifende Invariante: jede
// Mutation ist taktlängen-erhaltend (Summe der `<duration>` je Stimme konstant) – sonst wirft
// die monophone Konvertierung eine MeterException. Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht.
import {type NoteInfo, chordGroupOf, doublePartDivisions, findPartAndMeasureNote, getPartDivisions, getParts, getVoiceNotes, movePrimaryChildren, nextSmallerType, noteDurationTicks, parseMusicXml, removeBeat, serializeMusicXml, setHalvedDurationAndType, ticksToRestElements, ticksToTypeAndDots, voiceOf} from './core';
import {sanitizeEndingsInDoc} from './barlines';
import {expectedMeasureTicks} from './measureAttributes';
import {removeOutgoingBows} from './notes';

/** Wandelt eine `<note>` **in place** in eine gleich lange Pause um (längenerhaltend, MuseScore-
 *  Stil). Entfernt Tonhöhe/Chord-Marker/Artikulation, behält `<duration>`/`<voice>`/`<type>`/
 *  `<dot>`/`<staff>`/`<time-modification>`. Bereits-Pausen bleiben unverändert. Nur für
 *  **Primärnoten** gedacht – die `<chord/>`-Begleiter der Zählzeit entfernt `deleteNotesInMusicXml`
 *  separat (eine Pause hat keine Akkord-Begleiter). */
function convertNoteToRest(doc: Document, noteEl: Element): void {
    if (noteEl.querySelector('rest')) return;
    for (const el of noteEl.querySelectorAll('pitch, unpitched, chord, accidental, stem, notehead, beam, tie, lyric')) {
        el.remove();
    }
    for (const notations of [...noteEl.querySelectorAll('notations')]) {
        for (const child of [...notations.querySelectorAll('tied, slur')]) child.remove();
        if (!notations.children.length) notations.remove();
    }
    noteEl.insertBefore(doc.createElement('rest'), noteEl.firstChild);
}

/**
 * Löscht mehrere Noten **akkord-bewusst**. Ein Ton, dessen Akkord-Gruppe (`chordGroupOf`) noch
 * weitere, nicht gelöschte Töne enthält, wird **ersatzlos aus dem Akkord entfernt** – keine Pause,
 * die Zählzeit behält ihre Dauer über die verbleibenden Töne; fällt die Primärnote weg, rückt der
 * erste verbleibende Begleiter nach (verliert `<chord/>`, erbt `<beam>`/`<lyric>`, wie in
 * `reduceTriadsInMusicXml`). Bleibt an einer Zählzeit **kein** Ton übrig (Einzelnote, Pause oder
 * alle Akkordtöne ausgewählt), wird die Primärnote **in eine gleich lange Pause umgewandelt**
 * (`convertNoteToRest`) und ihre Begleiter werden entfernt – **genau eine** Pause je Zählzeit, nie
 * gestapelte. Ausgehende Bögen entfernter Töne werden samt Gegenstück gelöst (`removeOutgoingBows`).
 * So bleibt die Taktlängen-Invariante (Summe der Primärnoten-`<duration>` pro Stimme) erhalten:
 * ein ersatzloses Entfernen einer Primärnote verkürzte den Takt, eine Pause anstelle eines
 * `<chord/>`-Begleiters überfüllte ihn (beides `MeterException` im Backend). **Reihenfolge-
 * unabhängig**, weil alle Ziele **vor** der ersten Mutation aufgelöst werden (erst danach
 * verschieben sich `voiceNoteIndex`-Positionen). Eine als „Ganztakt löschen" gemeinte Auswahl (alle
 * Noten eines Takts) ergibt eine Pause je Zählzeit.
 */
export function deleteNotesInMusicXml(musicXml: string, infos: NoteInfo[]): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        // Phase 1: alle Ziele auflösen – noch keine Mutation, damit jeder voiceNoteIndex gültig bleibt.
        const targets = new Map<Element, { part: Element; voice: number }>();
        for (const info of infos) {
            const found = findPartAndMeasureNote(doc, info);
            if (found) targets.set(found.note, {part: found.part, voice: info.voice});
        }
        // Phase 2: je Akkord-Gruppe (= Zählzeit; Einzelnote = Gruppe der Länge 1) entscheiden.
        const handled = new Set<Element>();
        for (const [note, {part, voice}] of targets) {
            const group = chordGroupOf(note);
            const primary = group[0]!;
            if (handled.has(primary)) continue;
            handled.add(primary);
            const survivors = group.filter(m => !targets.has(m));
            const removed = group.filter(m => targets.has(m));
            if (!survivors.length) {
                // Kein Ton bleibt: Begleiter weg, Primärnote → gleich lange Pause.
                for (const el of removed) {
                    if (el === primary) continue;
                    removeOutgoingBows(part, el, voice);
                    el.remove();
                }
                convertNoteToRest(doc, primary);
                continue;
            }
            const newPrimary = survivors[0]!;
            if (newPrimary !== primary) {
                newPrimary.querySelector(':scope > chord')?.remove();
                movePrimaryChildren(primary, newPrimary);
            }
            for (const el of removed) {
                removeOutgoingBows(part, el, voice);
                el.remove();
            }
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error deleting notes in MusicXML:', e);
        return null;
    }
}

/** Löscht eine einzelne Note (= `deleteNotesInMusicXml` mit einem Element): einen Akkordton aus dem
 *  Akkord entfernen, sonst längenerhaltend in eine Pause umwandeln. */
export function deleteNoteInMusicXml(musicXml: string, noteInfo: NoteInfo): string | null {
    return deleteNotesInMusicXml(musicXml, [noteInfo]);
}

// Attribute, die in MusicXML über Takte hinweg **nachwirken** (gelten bis zur nächsten Definition).
// Beim Löschen ganzer Takte müssen sie in den ersten überlebenden Folgetakt migriert werden, falls
// ein gelöschter Takt sie geändert hat – sonst gingen z. B. die `<divisions>` (Dauer-Basis) verloren
// (→ falsche Dauern / Parse-Crash). Reihenfolge = canonical `<attributes>`-Kindreihenfolge.
export const CASCADE_ATTR_TAGS = ['divisions', 'key', 'time', 'staves', 'clef'];

/** Wendet die `<attributes>` eines Takts (in Dokumentreihenfolge) auf den laufenden Kaskaden-Zustand
 *  an (letzter Wert je Tag gewinnt). */
export function applyMeasureAttrs(measure: Element, state: Map<string, Element>): void {
    for (const attrs of measure.querySelectorAll('attributes')) {
        for (const child of [...attrs.children]) {
            if (CASCADE_ATTR_TAGS.includes(child.tagName)) state.set(child.tagName, child);
        }
    }
}

/** Fügt ein nachwirkendes Attribut (Klon) in canonical `<attributes>`-Reihenfolge in den Takt ein –
 *  aber nur, wenn der Takt es nicht ohnehin selbst definiert (dann gewinnt sein eigener Wert). */
export function injectCascadeAttr(doc: Document, measure: Element, tag: string, sourceEl: Element): void {
    for (const attrs of measure.querySelectorAll('attributes')) {
        if ([...attrs.children].some(c => c.tagName === tag)) return;
    }
    let attrs = measure.querySelector('attributes');
    if (!attrs) {
        attrs = doc.createElement('attributes');
        measure.insertBefore(attrs, measure.firstChild);
    }
    const clone = sourceEl.cloneNode(true) as Element;
    const order = CASCADE_ATTR_TAGS.indexOf(tag);
    let ref: Element | null = null;
    for (const child of [...attrs.children]) {
        if (CASCADE_ATTR_TAGS.indexOf(child.tagName) > order) {
            ref = child;
            break;
        }
    }
    if (ref) attrs.insertBefore(clone, ref);
    else attrs.appendChild(clone);
}

/** Nummeriert die Takte eines Parts fortlaufend neu und erhält dabei einen **Auftakt**: beginnt
 *  der Part mit `implicit="yes"` oder `number="0"`, behält der erste Takt die `0` und die Zählung
 *  startet erst beim zweiten Takt bei 1 – sonst verschöbe jede Struktur-Änderung (Takt löschen/
 *  einfügen) sämtliche gedruckten Taktnummern des Stücks um eins. */
export function renumberMeasures(part: Element): void {
    const measures = [...part.querySelectorAll('measure')];
    const first = measures[0];
    if (!first) return;
    const isPickup = first.getAttribute('implicit') === 'yes' || first.getAttribute('number') === '0';
    let n = isPickup ? 0 : 1;
    for (const m of measures) m.setAttribute('number', String(n++));
}

/**
 * Löscht **ganze Takte score-weit** (aus JEDEM Part, damit die Parts ausgerichtet bleiben) und
 * nummeriert die verbleibenden fortlaufend neu. Anders als das Noten-Löschen (Convert-to-Rest)
 * verkürzt das das Stück wirklich – gedacht für die **Ganztakt-Auswahl** (Freibereich-Klick).
 * **Nachwirkende `<attributes>`** (`divisions`/`key`/`time`/`clef`/`staves`), die ein gelöschter Takt
 * gesetzt hat, werden in den ersten überlebenden Folgetakt migriert – aber nur, wenn sich der
 * effektive Wert dadurch **ändert** (kein kosmetisches Neusetzen unveränderter Taktart o. Ä.). So
 * geht insbesondere `<divisions>` beim Löschen des ersten Takts nicht verloren. Gibt `null` zurück,
 * wenn dadurch **alle** Takte entfielen (das ganze Stück lässt sich nicht löschen) oder bei Fehler.
 * `measureIndices` adressiert neue interne Auswahlen eindeutig ordinal; ohne diesen Parameter bleibt
 * der nummernbasierte Legacy-Vertrag erhalten.
 */
export function deleteMeasuresInMusicXml(
    musicXml: string,
    measureNumbers: number[],
    measureIndices?: number[],
): string | null {
    try {
        if (!measureNumbers.length && !measureIndices?.length) return musicXml;
        const doc = parseMusicXml(musicXml);
        const toDelete = new Set(measureNumbers);
        const indicesToDelete = measureIndices ? new Set(measureIndices) : null;
        const numberOf = (m: Element) => parseInt(m.getAttribute('number') || '0', 10);
        const shouldDelete = (m: Element, index: number) => indicesToDelete
            ? indicesToDelete.has(index)
            : toDelete.has(numberOf(m));
        const serialize = (el: Element) => new XMLSerializer().serializeToString(el);

        for (const part of getParts(doc)) {
            const measures = [...part.querySelectorAll('measure')];
            if (!measures.length) continue;
            // Ganzes Part würde geleert → ablehnen (kein leerer Part / kein leeres Stück).
            if (measures.every((m, index) => shouldDelete(m, index))) return null;

            // Kaskaden-Zustand am START jedes Takts (aus den vorhergehenden Takten).
            const before: Map<string, Element>[] = [];
            const running = new Map<string, Element>();
            for (const m of measures) {
                before.push(new Map(running));
                applyMeasureAttrs(m, running);
            }

            // Überlebende von links nach rechts: nachwirkende Attribute in den Folgetakt migrieren,
            // wenn ein gelöschter Takt sie gegenüber dem letzten Überlebenden geändert hat.
            const leftInForce = new Map<string, Element>();
            for (let i = 0; i < measures.length; i++) {
                const m = measures[i]!;
                if (shouldDelete(m, i)) continue;
                const incoming = before[i]!;
                for (const tag of CASCADE_ATTR_TAGS) {
                    const should = incoming.get(tag);
                    if (!should) continue;
                    const will = leftInForce.get(tag);
                    if (will && serialize(will) === serialize(should)) continue; // Wert unverändert
                    injectCascadeAttr(doc, m, tag, should);
                }
                applyMeasureAttrs(m, leftInForce);
            }

            for (let i = 0; i < measures.length; i++) {
                const m = measures[i]!;
                if (shouldDelete(m, i)) m.remove();
            }
            renumberMeasures(part);
        }

        // Verwaiste Wiederholungs-/Volta-Marker der gelöschten Takte reparieren (sonst
        // verschmilzt music21 die unbalancierten Klammern beim Speichern-Roundtrip).
        sanitizeEndingsInDoc(doc);

        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error deleting measures in MusicXML:', e);
        return null;
    }
}

/**
 * Fügt einen **leeren, pausengefüllten Takt** vor/nach dem Referenztakt ein – score-weit in JEDEM
 * Part (Parts bleiben ausgerichtet). Die Pausenfüllung entspricht dem an der Einfügeposition
 * **geltenden Metrum** (Kaskade aus `divisions`/`time`), je **(voice, staff)-Paar** des
 * Referenztakts eine volle Stimme mit `<backup>` dazwischen – so bleiben die Taktlängen-Invariante
 * und die Grand-Staff-`<staff>`-Pflicht gewahrt. Beim Einfügen **vor Takt 0** werden die
 * nachwirkenden Attribute des alten ersten Takts in den neuen geklont (sonst stünden die
 * Pausen-`<duration>` vor jeder `<divisions>`-Definition – parst im Browser, crasht music21 beim
 * Speichern) und ein `implicit`-Auftakt wird regulär (ein Pickup mitten im Stück ist sinnlos).
 * Neue Takte bekommen keine Barlines; Marker des Referenztakts bleiben, wo sie sind (Einfügen
 * „danach" bei `:|` landet also außerhalb der Wiederholung). `null` bei ungültigem Index.
 */
export function insertMeasureInMusicXml(
    musicXml: string, measureIndex: number, position: 'before' | 'after',
): string | null {
    try {
        if (!Number.isInteger(measureIndex) || measureIndex < 0) return null;
        const doc = parseMusicXml(musicXml);
        const parts = getParts(doc);
        if (!parts.length || !parts[0]!.querySelectorAll('measure').item(measureIndex)) return null;

        for (const part of parts) {
            const measures = [...part.querySelectorAll('measure')];
            const reference = measures[measureIndex];
            if (!reference) continue;

            // Geltende nachwirkende Attribute AN DER EINFÜGEPOSITION: bei `after` (und beim
            // Einfügen VOR Takt 0, wo der neue Takt die Definitionen des alten ersten geklont
            // bekommt) einschließlich Referenztakt – bei `before` mitten im Stück dagegen NUR bis
            // zum Vortakt: definiert der Referenztakt selbst ein neues `<time>`/`<divisions>`,
            // gilt das erst AB ihm, nicht für den davor eingefügten Takt (sonst falsche
            // Pausenfüllung → MeterException beim music21-Roundtrip).
            const inclusiveUpTo = position === 'before' && measureIndex > 0 ? measureIndex - 1 : measureIndex;
            const state = new Map<string, Element>();
            for (let i = 0; i <= inclusiveUpTo; i++) applyMeasureAttrs(measures[i]!, state);

            const divisions = parseInt(state.get('divisions')?.textContent || '', 10) || getPartDivisions(part) || 1;
            const timeEl = state.get('time');
            const beats = parseInt(timeEl?.querySelector('beats')?.textContent || '4', 10) || 4;
            const beatType = parseInt(timeEl?.querySelector('beat-type')?.textContent || '4', 10) || 4;
            const ticks = expectedMeasureTicks(beats, beatType, divisions) || divisions * 4;

            // (voice, staff)-Paare des Referenztakts in Dokumentreihenfolge (Primärnoten ohne <chord/>).
            const pairs: { voice: number; staff: string | null }[] = [];
            const seen = new Set<string>();
            for (const n of reference.querySelectorAll(':scope > note')) {
                if (n.querySelector(':scope > chord')) continue;
                const voice = voiceOf(n);
                const staff = n.querySelector(':scope > staff')?.textContent ?? null;
                const key = `${voice}|${staff ?? ''}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    pairs.push({voice, staff});
                }
            }
            if (!pairs.length) pairs.push({voice: 1, staff: null});

            const newMeasure = doc.createElement('measure');
            pairs.forEach((pair, i) => {
                if (i > 0) {
                    const backup = doc.createElement('backup');
                    const dur = doc.createElement('duration');
                    dur.textContent = String(ticks);
                    backup.appendChild(dur);
                    newMeasure.appendChild(backup);
                }
                for (const rest of ticksToRestElements(doc, ticks, divisions, pair.voice, pair.staff)) {
                    newMeasure.appendChild(rest);
                }
            });

            if (position === 'before') {
                reference.before(newMeasure);
                if (measureIndex === 0) {
                    for (const tag of CASCADE_ATTR_TAGS) {
                        const el = state.get(tag);
                        if (el) injectCascadeAttr(doc, newMeasure, tag, el);
                    }
                    reference.removeAttribute('implicit');
                }
            } else {
                reference.after(newMeasure);
            }
            renumberMeasures(part);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error inserting measure in MusicXML:', e);
        return null;
    }
}

/** DOM-basierter Kern des „Note teilen": halbiert `refNote` und fügt eine bereinigte Klon-Hälfte
 *  direkt danach ein. Gibt den eingefügten Klon zurück (oder `null`, wenn bereits kleinster Typ /
 *  nicht teilbar). Verändert `doc`/`part` in place. */
export function splitNoteElement(doc: Document, part: Element, refNote: Element): Element | null {
    const type = refNote.querySelector('type')?.textContent || '';
    // Bereits kleinster Notentyp → nicht weiter teilbar.
    if (type && nextSmallerType(type) === null) return null;
    const newType = type ? nextSmallerType(type) : null;

    // Ganzzahligkeit der Halbierung sichern (selten nötig, z. B. bei bereits kurzen Noten).
    let duration = parseInt(refNote.querySelector('duration')?.textContent || '0', 10);
    if (duration % 2 !== 0) {
        doublePartDivisions(part);
        duration = parseInt(refNote.querySelector('duration')?.textContent || '0', 10);
    }
    const halfDuration = Math.floor(duration / 2);
    if (halfDuration < 1) return null;

    // Klon = zweite (neue) Note. Frisch machen: keine geerbte Farbe, keine Beams,
    // keine Bögen/Haltebögen (Split ergibt zwei getrennte Noten).
    const clone = refNote.cloneNode(true) as Element;
    clone.removeAttribute('color');
    for (const beam of clone.querySelectorAll('beam')) beam.remove();
    for (const tie of clone.querySelectorAll('tie')) tie.remove();
    for (const notations of clone.querySelectorAll('notations')) {
        for (const child of [...notations.querySelectorAll('tied, slur')]) child.remove();
        if (!notations.children.length) notations.remove();
    }

    // Beide Noten auf die halbe Dauer/Typ setzen; Original-Beams entfernen (sonst
    // inkonsistente Balken).
    for (const beam of refNote.querySelectorAll('beam')) beam.remove();
    setHalvedDurationAndType(refNote, halfDuration, newType);
    setHalvedDurationAndType(clone, halfDuration, newType);

    // Klon direkt nach dem Original einfügen.
    refNote.after(clone);
    return clone;
}

/**
 * „Note teilen": halbiert die Dauer der Referenznote und fügt eine zweite Note gleicher Tonhöhe
 * direkt danach ein. Die Taktlänge bleibt dadurch exakt erhalten (wichtig, damit die spätere
 * monophone Konvertierung keinen überfüllten Takt / `MeterException` bekommt). Funktioniert
 * auch für Pausen (`<rest>`). Gibt das serialisierte MusicXML zurück oder `null` bei Fehler /
 * nicht teilbarer Note (kleinster Notentyp).
 */
export function splitNoteInMusicXml(musicXml: string, noteInfo: NoteInfo): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        if (!splitNoteElement(doc, found.part, found.note)) return null;
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error splitting note in MusicXML:', e);
        return null;
    }
}

/** Setzt `<duration>`, `<type>` und die Punktierung einer `<note>`. `<dot>` landet schema-korrekt
 *  direkt hinter `<type>`; fehlt `<type>`, wird es nach `<voice>` (bzw. `<duration>`) eingefügt. */
function setDurationTypeAndDots(doc: Document, noteEl: Element, ticks: number, type: string, dots: number): void {
    const durEl = noteEl.querySelector('duration');
    if (durEl) durEl.textContent = ticks.toString();
    for (const d of [...noteEl.querySelectorAll('dot')]) d.remove();
    let typeEl = noteEl.querySelector('type');
    if (!typeEl) {
        typeEl = doc.createElement('type');
        const before = noteEl.querySelector('voice') ?? durEl;
        if (before) before.after(typeEl); else noteEl.appendChild(typeEl);
    }
    typeEl.textContent = type;
    let anchor: Element = typeEl;
    for (let i = 0; i < dots; i++) {
        const dot = doc.createElement('dot');
        anchor.after(dot);
        anchor = dot;
    }
}

/**
 * DOM-Kern des „Note/Pause mit der nächsten zusammenführen": fasst die Akkord-Gruppe von `noteEl` mit
 * der **direkt folgenden** Gruppe derselben Stimme im selben Takt zu **einer** Zählzeit mit der
 * Summendauer zusammen. Überlebende Gruppe = die erste **klingende** der beiden (Pause + Note → die
 * Note rückt an den Onset der Pause; Pause + Pause → Pause). Gibt die Primär-Note der überlebenden
 * Gruppe zurück oder `null` (keine Folgenote im Takt, Vorschlag/Triole, Summe nicht als eine
 * (punktierte) Note darstellbar). Verändert `doc`/`part` in place.
 */
function mergeNoteWithNextElement(doc: Document, part: Element, measure: Element, noteEl: Element): Element | null {
    const voice = voiceOf(noteEl);
    const voiceNotes = getVoiceNotes(measure, voice);
    const group = chordGroupOf(noteEl);
    const next = voiceNotes[voiceNotes.indexOf(group[group.length - 1]!) + 1];
    if (!next) return null;                                   // letzte Note im Takt
    const nextGroup = chordGroupOf(next);

    const run = [...group, ...nextGroup];
    if (run.some(n => n.querySelector('grace') || n.querySelector('time-modification'))) return null;

    const total = noteDurationTicks(group[0]!) + noteDurationTicks(nextGroup[0]!);
    const shape = ticksToTypeAndDots(total, getPartDivisions(part));
    if (!shape) return null;

    const survivor = !group[0]!.querySelector('rest') || nextGroup[0]!.querySelector('rest') ? group : nextGroup;
    const removed = survivor === group ? nextGroup : group;

    // Ausgehende Bögen beider Gruppen (samt stop-Gegenstück) lösen – eingehende der überlebenden Note
    // bleiben; Beams wie beim Teilen entfernen (music21 re-beamt beim Export).
    for (const n of run) removeOutgoingBows(part, n, voice);
    if (survivor === nextGroup) group[0]!.before(...nextGroup);
    removeBeat(removed[0]!);
    for (const n of survivor) {
        for (const beam of [...n.querySelectorAll('beam')]) beam.remove();
        setDurationTypeAndDots(doc, n, total, shape.type, shape.dots);
    }
    return survivor[0]!;
}

/**
 * „Note/Pause mit der nächsten zusammenführen" (Gegenstück zu {@link splitNoteInMusicXml}): führt die
 * Note/Pause mit der **direkt folgenden derselben Stimme im selben Takt** zu einer Note mit der
 * Summendauer zusammen (2 Achtel → Viertel, Viertel + Achtel → punktierte Viertel). Die Note behält ihre
 * Tonhöhe; ist sie eine Pause und die Folgenote klingt, übernimmt das Ergebnis deren Tonhöhe.
 * Längenerhaltend (Taktdauer bleibt gültig). `null`, wenn die Note die letzte im Takt ist, Triolen/
 * Vorschläge beteiligt sind oder die Summe keine einzelne (0–2× punktierte) Note ergibt.
 */
export function mergeNoteWithNextInMusicXml(musicXml: string, noteInfo: NoteInfo): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        if (!mergeNoteWithNextElement(doc, found.part, found.measure, found.note)) return null;
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error merging note in MusicXML:', e);
        return null;
    }
}

